import Style from './useMonitoringFormViewModal.module.scss';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useModal } from 'src/application/hooks/useModal';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { OverviewNavbar, OverviewNavbarItemProps } from 'uikit/fields/overview/OverviewNavbar';
import { useStaffProfile, useStudy } from 'src/helpers/hooks/useStudy';
import { ModalToolbar } from 'src/features/generalForms/uikit/modalToolbar/modalToolbar';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { IMonitoringFormResultDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useMonitoringFormFillingModal } from './useMonitoringFormFillingModal';
import { GeneralFormAnswer } from 'src/features/generalForms/answerPreview/generalFormAnswer';
import { useForm } from 'react-hook-form';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { monitoringFormsAnswerControlsResolver } from 'src/features/generalForms/monitoringForms/answerResolver';
import { triggerDownloadFileDialog } from 'src/features/report/report-helpers';
import { generateConfigurableMonitoringFormsReport } from 'src/features/report';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { normalizeFileName } from 'src/helpers/string-helper';
import {
  useGetMonitoringFormConfigQuery,
  useGetMonitoringFormResultQuery,
  useGetMonitoringFormResultsQuery,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsQuery';
import { Loading } from 'uikit/suspense/Loading';

export const useMonitoringFormViewModal = () => {
  const modal = useModal();
  const [formResultId, setFormResultId] = useState<number | undefined>();

  const closeModalHandler = () => {
    modal.closeModal();
    setFormResultId(undefined);
  };

  return {
    openHandler: (resultId: number) => {
      modal.openModal();
      setFormResultId(resultId);
    },
    modal: modal.visible && formResultId && (
      <MonitoringFormViewModal initialFormResultId={formResultId} onClose={closeModalHandler} />
    ),
  };
};

const MonitoringFormViewModal: FC<{
  initialFormResultId: number;
  onClose: () => void;
}> = ({ onClose, initialFormResultId }) => {
  const study = useStudy();
  const { hasStudyClaim } = useHasStudyClaims();
  const { profile } = useStaffProfile();

  const [resultId, setResultId] = useState(initialFormResultId);

  const { data: resultDto, isFetching: resultLoading } = useGetMonitoringFormResultQuery(
    { resultId: resultId },
    { keepPreviousData: true },
  );
  const { data: resultsForNavbar, isFetching: resultsForNavbarLoading } = useGetMonitoringFormResultsQuery(
    { formId: resultDto?.formConfigId ?? 0 },
    { enabled: !!resultDto?.formConfigId, keepPreviousData: true },
  );
  const { data: formConfig, isFetching: configLoading } = useGetMonitoringFormConfigQuery(
    { formId: resultDto?.formConfigId ?? 0 },
    { enabled: !!resultDto?.formConfigId, keepPreviousData: true },
  );
  const formFillingModal = useMonitoringFormFillingModal();

  const navbarItemsProps: OverviewNavbarItemProps[] = useMemo(
    () =>
      resultsForNavbar?.map(
        (result, _, arr) =>
          ({
            key: result.formResultId,
            selected: result.formResultId === resultId,
            text: `#${result.formResultId}`,
            disabled: arr.length < 2 && result.formResultId === resultId,
            onClick: () => setResultId(result.formResultId),
          } satisfies OverviewNavbarItemProps),
      ) ?? [],
    [resultsForNavbar, resultId],
  );

  const form = useForm<DataType>({
    defaultValues: resultDto?.value ?? {},
  });
  useEffect(() => {
    form.reset(resultDto?.value ?? {});
  }, [form, resultDto?.value]);

  const onEdit = useCallback(
    (result: IMonitoringFormResultDto) => {
      if (!formConfig) throw new Error('MonitoringFormsConfig has not provided');
      formFillingModal.edit(result.formResultId, result.formConfigId);
    },
    [formFillingModal, formConfig],
  );

  const onExport = useCallback(async () => {
    if (!resultDto || !profile || !study || !formConfig)
      throw new Error('Not all data provided for exporting Monitoring Forms Report');

    const blob = await generateConfigurableMonitoringFormsReport({
      resultsWithConfig: [{ result: resultDto, config: formConfig }],
      studyName: study.title,
      studyNumber: study.studyNumber,
      authorFullName: profile.fullName,
    });

    const fileName = `Study-${study.studyNumber}-${formConfig.title}-${localFormat(new Date(), DateFormats.forFiles)}`;

    triggerDownloadFileDialog(blob, normalizeFileName(fileName));
  }, [formConfig, profile, resultDto, study]);

  if (!resultDto || !formConfig) return null;

  return (
    <>
      <DialogModal bodyClassName={Style.modalBody} visible={true} onHide={onClose} title={formConfig?.title}>
        <Loading
          containerClassName={Style.loaderContainer}
          renderChildren={true}
          loading={resultLoading || resultsForNavbarLoading || configLoading}
          testId={'monitoring-form-view-modal'}
        >
          <div>
            {!!navbarItemsProps.length && <OverviewNavbar items={navbarItemsProps} className={Style.navBar} />}
            <ModalToolbar
              className={Style.toolbar}
              onExport={onExport}
              onEdit={hasStudyClaim(StudyRoleClaimValue.MonitoringReportCreate) ? () => onEdit(resultDto) : undefined}
              created={{
                at: resultDto.createdAt,
                fullName: resultDto.createdBy.fullName,
              }}
              updated={{
                at: resultDto.updatedAt,
                fullName: resultDto.updatedBy.fullName,
              }}
            />
          </div>

          <GeneralFormAnswer
            form={form}
            resolver={monitoringFormsAnswerControlsResolver}
            defaultLayout={formConfig.layout}
            initialScoreSections={formConfig.scoreSections ?? []}
            initialVariables={formConfig.variables ?? []}
          />
        </Loading>
      </DialogModal>
      {formFillingModal.modal}
    </>
  );
};
